import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { BackTop, useScrollTop, Breadcrumb } from "@/components/feature/UI";
import { faqGroups, faqItems } from "@/mocks/faq";
import { siteName, useLdJson, useSeo } from "@/hooks/useSeo";

export default function FaqPage() {
  useScrollTop();
  const [active, setActive] = useState(faqGroups[0]);
  const [open, setOpen] = useState<number | null>(0);

  useSeo({
    title: `常见问题 FAQ - ${siteName} | 投稿安全与内容原则`,
    description: `${siteName}常见问题解答：内容来源是否真实、匿名投稿是否安全、转载授权规则与平台立场，帮你放心理性吃瓜。`,
    keywords: "每日新瓜,常见问题,匿名投稿,内容来源,转载授权,理性吃瓜",
  });

  useLdJson(
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((it) => ({
        "@type": "Question",
        name: it.q,
        acceptedAnswer: { "@type": "Answer", text: it.a },
      })),
    },
    "faq-ld"
  );

  const list = faqItems.filter((it) => it.group === active);

  return (
    <>
      <Header />
      <main>
        <section className="border-b border-background-200 bg-background-100/60">
          <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6">
            <Breadcrumb items={[{ label: "常见问题" }]} />
            <div className="mt-6">
              <span className="font-label text-xs font-semibold uppercase tracking-[0.3em] text-primary-600">
                FAQ · 常见问题
              </span>
              <h1 className="font-display mt-3 text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                关于本站，先问清楚再吃瓜
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-600 md:text-base">
                投稿是否安全？内容是否真实？转载要注意什么？我们把最高频的问题整理成册，一次说清楚。
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-[1200px] grid-cols-1 gap-8 px-4 py-10 md:px-6 lg:grid-cols-4">
          <aside className="lg:col-span-1">
            <div className="flex flex-row flex-wrap gap-2 lg:flex-col">
              {faqGroups.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => {
                    setActive(g);
                    setOpen(0);
                  }}
                  className={`flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm transition-colors lg:justify-start ${
                    active === g
                      ? "bg-primary-500 font-medium text-background-50"
                      : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  <i className={active === g ? "ri-focus-3-line" : "ri-question-line"}></i>
                  {g}
                </button>
              ))}
              <Link
                to="/contact"
                className="mt-1 flex items-center gap-2 whitespace-nowrap rounded-md border border-dashed border-primary-300 px-4 py-2.5 text-sm text-primary-600 transition-colors hover:bg-primary-100/50"
              >
                <i className="ri-send-plane-line"></i>
                没解决？去投稿提问
              </Link>
            </div>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex flex-col gap-3">
              {list.map((it, i) => {
                const isOpen = open === i;
                return (
                  <div key={it.q} className="overflow-hidden rounded-lg border border-background-200/80 bg-background-50">
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                    >
                      <h2 className="font-display flex items-center gap-3 text-sm font-bold leading-snug text-foreground-950 md:text-base">
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${isOpen ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-700"}`}>
                          Q
                        </span>
                        {it.q}
                      </h2>
                      <i className={`shrink-0 text-foreground-400 transition-transform ${isOpen ? "rotate-180" : ""} ri-arrow-down-s-line`}></i>
                    </button>
                    {isOpen && (
                      <p className="px-5 pb-5 pl-[52px] text-sm leading-7 text-foreground-600">{it.a}</p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-100/60 p-5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                <i className="ri-chat-smile-3-line text-sm"></i>
              </span>
              <div className="text-sm leading-6 text-foreground-700">
                <p className="font-semibold text-foreground-950">还有想问的？</p>
                <p className="mt-1">
                  欢迎通过投稿页面向编辑部提问或提供线索，也可发送邮件至 dailygossip@example.com，我们会尽快回复。
                </p>
                <Link to="/contact" className="mt-2 inline-flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700">
                  前往投稿提问
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackTop />
    </>
  );
}